import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { CloudOff, RefreshCw, AlertTriangle, CheckCircle, Clock } from './icons';
import {
  useNetworkStatus,
  useSyncStatusQuery,
  useSyncMutation,
  useOfflineStatsQuery,
  useRetryFailedMutation,
  useOfflineTime
} from '../hooks/queries/useOfflineRequestsQuery';


interface SyncStatusProps {
  style?: any;
  showDetails?: boolean;
  hideButtons?: boolean;
}

export const SyncStatus: React.FC<SyncStatusProps> = ({
  style,
  showDetails = true,
  hideButtons = false
}) => {
  const isOnline = useNetworkStatus();
  const { data: syncStatus } = useSyncStatusQuery();
  const { data: offlineStats } = useOfflineStatsQuery();
  const syncMutation = useSyncMutation();
  const retryFailedMutation = useRetryFailedMutation();
  const offlineTime = useOfflineTime();

  const handleSync = async () => {
    if (!isOnline) return;
    if (syncMutation.isPending || retryFailedMutation.isPending) return;

    // Use React Query mutation for sync
    try {
      syncMutation.mutate(undefined, {
        onError: (error) => {
          console.error('SyncStatus: Sync failed:', error);
        }
      });
    } catch (error) {
      console.error('SyncStatus: Error in handleSync:', error);
    }
  };

  const handleRetryFailed = async () => {
    if (!isOnline) return;
    if (syncMutation.isPending || retryFailedMutation.isPending) return;

    try {
      retryFailedMutation.mutate(undefined, {
        onError: (error) => {
          console.error('SyncStatus: Retry failed:', error);
        }
      });
    } catch (error) {
      console.error('SyncStatus: Error in handleRetryFailed:', error);
    }
  };

  const getStatusColor = () => {
    if (!isOnline) return '#dc3545'; // Red for offline
    if (syncStatus?.pendingCount || syncStatus?.failedCount) return '#ffc107'; // Yellow for pending
    return '#28a745'; // Green for all good
  };

  const getStatusIcon = () => {
    const iconProps = { size: 16, color: getStatusColor() };
    
    if (!isOnline) return <CloudOff {...iconProps} />;
    if (syncStatus?.isSyncing) return <RefreshCw {...iconProps} />;
    if (syncStatus?.failedCount) return <AlertTriangle {...iconProps} />;
    if (syncStatus?.pendingCount) return <Clock {...iconProps} />;
    return <CheckCircle {...iconProps} />;
  };

  const getStatusText = () => {
    if (!isOnline) {
      const offlineTimeText = offlineTime ? ` since: ${formatOfflineTime(offlineTime)}` : '';
      return `Offline${offlineTimeText}`;
    }
    if (syncStatus?.isSyncing) return 'Syncing...';
    if (syncStatus?.failedCount) return 'Sync failed';
    if (syncStatus?.pendingCount) return 'Pending sync';
    return isOnline ? 'Online - All synced' : 'All synced';
  };

  const formatOfflineTime = (timestamp: number | null) => {
    if (!timestamp) return null;

    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 0) return `${days} day${days > 1 ? 's' : ''}`;
    if (hours > 0) return `${hours} hour${hours > 1 ? 's' : ''}`;
    if (minutes > 0) return `${minutes} min${minutes > 1 ? 's' : ''}`;
    return '< 1 min';
  };

  return (
    <View style={[styles.container, style]} testID="sync-status-container">
      {/* Main status row */}
      <View style={styles.statusRow}>
        <View style={styles.statusInfo} testID="status-indicator">
          {getStatusIcon()}
          <Text style={[styles.statusText, { color: getStatusColor() }]}>
            {getStatusText()}
          </Text>
        </View>
        
        {!hideButtons && (
          <View style={styles.buttonContainer}>
            {/* Retry Failed Button - only show when there are failed requests */}
            {syncStatus?.failedCount > 0 && (
              <TouchableOpacity
                style={[
                  styles.retryButton,
                  (!isOnline || retryFailedMutation.isPending || syncMutation.isPending) && styles.retryButtonDisabled
                ]}
                onPress={handleRetryFailed}
                disabled={!isOnline || retryFailedMutation.isPending || syncMutation.isPending}
                testID="retry-failed-button"
              >
                {retryFailedMutation.isPending ? (
                  <ActivityIndicator size="small" color="#fff" />
                ) : (
                  <AlertTriangle size={14} color="#fff" />
                )}
                <Text style={styles.retryButtonText}>
                  {retryFailedMutation.isPending ? 'Retrying' : 'Retry Failed'}
                </Text>
              </TouchableOpacity>
            )}

            {/* Regular Sync Button */}
            <TouchableOpacity
              style={[
                styles.syncButton,
                (!isOnline || syncStatus?.isSyncing || syncMutation.isPending || retryFailedMutation.isPending) && styles.syncButtonDisabled
              ]}
              onPress={handleSync}
              disabled={!isOnline || syncStatus?.isSyncing || syncMutation.isPending || retryFailedMutation.isPending}
              testID="sync-button"
            >
              {(syncStatus?.isSyncing || syncMutation.isPending) ? (
                <ActivityIndicator size="small" color="#fff" />
              ) : (
                <RefreshCw size={14} color="#fff" />
              )}
              <Text style={styles.syncButtonText}>
                {syncStatus?.isSyncing || syncMutation.isPending ? 'Syncing' : 'Sync'}
              </Text>
            </TouchableOpacity>
          </View>
        )}
      </View>

      {/* Details section */}
      {showDetails && (
        <View style={styles.details}>
          {/* Stats row */}
          <View style={styles.statsRow}>
            {syncStatus?.pendingCount > 0 && (
              <Text style={styles.detailText}>
                {syncStatus.pendingCount} pending
              </Text>
            )}
            {syncStatus?.failedCount > 0 && (
              <Text style={[styles.detailText, styles.errorText]}>
                {syncStatus.failedCount} failed
              </Text>
            )}
          </View>

        </View>
      )}

      {/* Error message for sync failures */}
      {(syncMutation.error || retryFailedMutation.error) && (
        <View style={styles.errorContainer}>
          <AlertTriangle size={14} color="#dc3545" />
          <Text style={styles.errorMessage}>
            {syncMutation.error instanceof Error
              ? syncMutation.error.message
              : retryFailedMutation.error instanceof Error
              ? retryFailedMutation.error.message
              : 'Operation failed'}
          </Text>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  statusRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  statusInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  statusText: {
    fontSize: 14,
    fontWeight: '500',
    marginLeft: 8,
  },
  buttonContainer: {
    flexDirection: 'row',
    gap: 8,
  },
  syncButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#007bff',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    gap: 4,
  },
  syncButtonDisabled: {
    backgroundColor: '#6c757d',
  },
  syncButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '500',
  },
  retryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#dc3545',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 4,
    gap: 4,
  },
  retryButtonDisabled: {
    backgroundColor: '#6c757d',
  },
  retryButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '500',
  },
  details: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#e9ecef',
  },
  statsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  detailText: {
    fontSize: 12,
    color: '#6c757d',
  },
  errorText: {
    color: '#dc3545',
  },
  offlineText: {
    fontSize: 12,
    color: '#dc3545',
    fontStyle: 'italic',
    marginTop: 4,
  },
  errorContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#dc3545',
    gap: 6,
  },
  errorMessage: {
    fontSize: 12,
    color: '#dc3545',
    flex: 1,
  },
});